import { collection, query, where, orderBy, getDocs, doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Service for managing the Firestore-backed template library.
 * Templates are stored in the `templates` collection and are publicly readable.
 * Only admin users can write (enforced by Firestore rules + Cloud Function validation).
 */

/** Fetch all enabled templates, ordered by sortOrder. */
export async function getFirestoreTemplates() {
  try {
    const q = query(
      collection(db, 'templates'),
      where('enabled', '==', true),
      orderBy('sortOrder', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching templates:', error);
    return [];
  }
}

/** Fetch enabled templates flagged for the landing page. */
export async function getLandingTemplates() {
  try {
    const q = query(
      collection(db, 'templates'),
      where('enabled', '==', true),
      where('showOnLanding', '==', true),
      orderBy('sortOrder', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching landing templates:', error);
    return [];
  }
}

/** Check if a campaign is already in the template library. */
export async function getTemplateByCampaignId(campaignId) {
  try {
    const q = query(
      collection(db, 'templates'),
      where('campaignId', '==', campaignId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const d = snapshot.docs[0];
    return { id: d.id, ...d.data() };
  } catch (error) {
    console.error('Error checking template:', error);
    return null;
  }
}

/** Add a campaign as a template (client-side write — admin only via rules). */
export async function addTemplate(data) {
  try {
    const templateRef = doc(collection(db, 'templates'));
    const templateData = {
      name: data.name,
      description: data.description || '',
      campaignId: data.campaignId,
      gameType: data.gameType,
      category: data.category || 'marketing',
      useCase: data.useCase || '',
      imageUrl: data.imageUrl || '',
      accent: data.accent || '',
      bg: data.bg || '',
      bgSecondary: data.bgSecondary || '',
      showOnLanding: data.showOnLanding || false,
      enabled: true,
      sortOrder: data.sortOrder || 10,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: data.createdBy || '',
    };
    await setDoc(templateRef, templateData);
    return { id: templateRef.id, ...templateData };
  } catch (error) {
    console.error('Error adding template:', error);
    throw error;
  }
}

/** Update an existing template. */
export async function updateTemplate(templateId, updates) {
  try {
    const ref = doc(db, 'templates', templateId);
    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
}

/** Soft-delete a template (set enabled: false). */
export async function removeTemplate(templateId) {
  try {
    const ref = doc(db, 'templates', templateId);
    await updateDoc(ref, { enabled: false, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error('Error removing template:', error);
    throw error;
  }
}
